import { Box, Button, Grid2, Input, Menu, MenuItem, styled, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import FormCard from '../FormCard';
import Context from '../../context';
import { useGetToken } from '../../hooks/useGetToken';
import { Constants, fbGetForm, fbGetForms, fbGetFrontline, fbPostImport, FileType } from '../../services/service';
import { useNavigate } from 'react-router-dom'; // Update to useNavigate
import LoadingSpinner from '../LoadingSpinner';
import Popup from '../Popup';
import { FormContextProvider } from '../../formContext';
import { PopupContextProvider } from '../../PopupContext';
import IconGallery from '../Icons/iconGallery';
import { FbeFrontline, FbeFrontlineInfo, getLanguageDisplayName } from '../../services/fbeTypes';
import { fbImportFailureMessage, fbImportSuccessMessage } from '../FormCardMenu';
import { AxiosResponse } from 'axios';
import { getUserGroups } from '../../utils/getUserGroups';

const REACT_APP_IES_LOCAL_ACCESS =
  process.env.REACT_APP_IES_LOCAL_ACCESS || 'invalidLocal';
const REACT_APP_IES_GLOBAL_ACCESS =
  process.env.REACT_APP_IES_GLOBAL_ACCESS || 'invalidGlobal';

  const ButtonStyled = styled(Button)({
    borderRadius: '24px',
    padding: '6px 50px',
  });

const ListView: React.FC = () => {
  const [getTokenFunction] = useGetToken();
  const {
    authState,
    updateIsLoading,
    isLoading,
    setShowError,
    setMessage: setStatusMessage,
    setShowSuccess
  } = useContext(Context);

  const { user, graph } = authState;

  const [cardData, setCardData] = useState<any[]>([]);

  const [frontline, setFrontline] = useState<FbeFrontline | null>(null);

  const navigate = useNavigate(); // Update to useNavigate

  const userGroups = getUserGroups(user?.idTokenClaims?.['groups'], graph?.groups);
  const isLocalAdmin =
    (userGroups as string[]).includes(REACT_APP_IES_LOCAL_ACCESS) &&
    !(userGroups as string[]).includes(REACT_APP_IES_GLOBAL_ACCESS);

  const isGlobalAdmin =
    !(userGroups as string[]).includes(REACT_APP_IES_LOCAL_ACCESS) &&
    (userGroups as string[]).includes(REACT_APP_IES_GLOBAL_ACCESS);
  const [languages, setLanguages] = useState<string[]>(['en']);

  useEffect(() => {
    const formatDateAndTime = (dateString: string) => {
      const dateObj = new Date(dateString);
      const date = `${dateObj.getDate().toString().padStart(2, '0')}.${(
        dateObj.getMonth() + 1
      )
        .toString()
        .padStart(2, '0')}.${dateObj.getFullYear()}`;
      const time = `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj
        .getMinutes()
        .toString()
        .padStart(2, '0')}`;
      return { date, time };
    };

    const getTextByLanguage = (
      language: string,
      translations: { language: string; text: string }[],
      notFound: string,
    ): string => {
      const translation = translations.find(
        (item) => item.language === language
      );
      return translation ? translation.text : notFound;
    };

    const fetchCardData = async () => {
      try {
        updateIsLoading(true);
        const language = 'en';
        const accessToken = await getTokenFunction();
        const unsortedFormRes = await fbGetForms(accessToken);
        const formRes = unsortedFormRes.sort( (a,b) => a.name[0].text.toLowerCase() > b.name[0].text.toLowerCase() ? 1 : -1);

        const modifiedCardData = formRes.map((item: any) => {
          const { date: createdDate, time: createdTime } = formatDateAndTime(
            item.createdAt
          );
          const { date: updatedDate, time: updatedTime } = formatDateAndTime(
            item.modifiedAt
          );
          let formType = item.formType;
          if (isLocalAdmin && item.pk === Constants.FORM_GLOBAL) {
            formType = Constants.LOCKED;
          }
          const title = getTextByLanguage(language, item.name || [], item.name.length > 0 ? item.name[0].text : 'Text not available');
          const languages = item.languages || [language];
          setLanguages(languages);
          return {
            ...item,
            title,
            createdDate,
            createdTime,
            updatedDate,
            updatedTime,
            formType,
            accessToken,
            isGlobalAdmin,
            isLocalAdmin,
            languages
          };
        });
        setCardData(modifiedCardData);
        updateIsLoading(false);

        const fbGetSettingsSuccessMessage = 'Retrieved the settings!';
        const fbGetSettingsErrorMessage = 'Unable to retrieve settings!';

        const frontlineData = await fbGetFrontline(accessToken);
        if(frontlineData){
          setFrontline(frontlineData);
          setStatusMessage(fbGetSettingsSuccessMessage);
          setShowSuccess(true);
        } else{
          setStatusMessage(fbGetSettingsErrorMessage);
          setShowError(true);
        }

      } catch (e) {
        setStatusMessage(`fbGetForms ${e}`);
        setShowError(true);
      } finally {
        updateIsLoading(false);
      }
    };

    fetchCardData();
  }, []);

  useEffect(() => {
    // redraw when frontline is read
//    console.error(`frontline ${JSON.stringify(frontline)}`);
  }, [frontline]);

  const [anchorElCreate, setAnchorElCreate] = useState<null | HTMLElement>(null);

  const handleClickCreate = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElCreate(event.currentTarget); // Set anchor to open menu
  };

  const handleCloseCreate = () => {
    setAnchorElCreate(null);
    setSubmenuAnchorEl(null);
    setSubmenuAnchorEl(null);
    setSubmenuAnchorQuestionsEl(null);
  };


  const [fileType, setFileType] = useState<FileType>(FileType.FORM);
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(undefined);

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files ? event.target.files[0] : '';

    if (selectedFile) {
      updateIsLoading(true);
      const accessToken = await getTokenFunction();

      const formData = new FormData();
      formData.append('file', selectedFile);

      try {
        const fileName = selectedFile.name;

        const sk = fileType === FileType.FORM ? '_new_' : fileName.split('_')[0];

        if(frontline && frontline.frontline !== undefined){
          const info = frontline.settings[frontline.frontline as keyof typeof frontline] as unknown as FbeFrontlineInfo;
          const referenceId = `form_${frontline.frontline}%23${sk}%23${selectedLanguage  || info.languages[0]}`;

          const response = await fbPostImport(
            fileType,
            referenceId,
            formData,
            accessToken);
          if(response && response.status === 200){
            setStatusMessage(fbImportSuccessMessage);
            setShowSuccess(true);
            const newForm = await fbGetForm(response.data.pk, response.data.sk, accessToken);
            updateIsLoading(false);
            navigate('/edit', { state: { formData: newForm } });
          } else {
            const moreInfo = (fileType ===FileType.QUESTIONS) ? response?.data : ''
            setStatusMessage(`${fbImportFailureMessage} ${moreInfo}`);
            setShowError(true);
          }
        } else {
          setStatusMessage(fbImportFailureMessage);
          setShowError(true);
      }
      } catch(error){
        const moreInfo = (fileType ===FileType.QUESTIONS) ? (error as AxiosResponse)?.data : ''
        setStatusMessage(`${fbImportFailureMessage} ${moreInfo}`);
        setShowError(true);
      }
      updateIsLoading(false);
    }
  }
  const [submenuAnchorQuestionsEl, setSubmenuAnchorQuestionsEl] = useState<null | HTMLElement>(null);

  const handleSubmenuQuestionsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSubmenuAnchorQuestionsEl(event.currentTarget);
  };

  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSubmenuAnchorEl(event.currentTarget);
  };

  const handleCreate = async (
    fileType: FileType,
    language: string
  ) => {
    setSelectedLanguage(language);
    if(fileType === FileType.NONE){
      navigate(`/create?language=${language}`);
    } else if(fileType === FileType.FORM){
      const fileInput = document.getElementById('create_file_input') as HTMLInputElement | null;
      fileInput?.click();
      setFileType(fileType);
    } else if(fileType === FileType.QUESTIONS){
      const fileInput = document.getElementById('create_file_input') as HTMLInputElement | null;
      fileInput?.click();
      setFileType(fileType);
    }
    handleCloseCreate();
  }

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div data-testid="ListView" style={{ flexGrow: 1}}>
          <div style={{ paddingLeft: '0px', paddingRight: '0px', marginTop:'42px' }}>
        <hr
          style={{
            border: '0',
            borderTop: '1px solid #d3d3d3',
            margin: 0,
            width: '100%',
          }}
        />
      </div>
          <Grid2
            container
            spacing={4}
            sx={{ width: '100%', padding: '16px 8px'  }}
          >
            <Grid2 size="grow">
              <div style={{ display: 'flex' }}>
                <Typography
                  variant="h5"
                  sx={{ whiteSpace: 'nowrap', marginLeft: '8px' }}
                >
                  Forms ({cardData.length})
                </Typography>
              </div>
            </Grid2>
            <Grid2 size="grow" sx={{ textAlign: 'right', marginRight:'40px' }}>
              <div style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                <IconGallery
                  data-testid="IconGallery"
                  cursor="pointer"
                  onClick={() => {
                    navigate('/images');
                  }}
                />
              </div>
            </Grid2>
          </Grid2>
          <Box p={4}>
            <PopupContextProvider>
            <FormContextProvider>
              <Grid2 container spacing={4}>
                {cardData.map((data, index) => (
                  <Grid2 size={{ xs: 12, sm: 4 }} key={index}>
                    <FormCard
                      frontline={frontline ? frontline : undefined}
                      pk={data.pk}
                      sk={data.sk}
                      accessToken={data.accessToken}
                      createdDate={data.createdDate}
                      createdTime={data.createdTime}
                      createdBy={data.createdByName || 'Unknown'}
                      updatedDate={data.updatedDate}
                      updatedTime={data.updatedTime}
                      updatedBy={data.modifiedByName || 'Unknown'}
                      status={data.status}
                      activeStatus={data.activeStatus}
                      title={data.title}
                      description={data.description || 'No Description'}
                      roles={data.role}
                      formType={data.formType}
                      isExtension={data.isExtension}
                      parentFormName={data.parentFormName}
                      hasExtension={data.hasExtension}
                      isGlobalAdmin={data.isGlobalAdmin}
                      isLocalAdmin={true}
                      active={data.active}
                      languages={data.languages}
                    />
                  </Grid2>
                ))}
              </Grid2>
              <Popup initialTrigger={false} />
            </FormContextProvider>
            </PopupContextProvider>

            {/* Create New Form Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginTop: '20px',
              }}
            >
              <ButtonStyled
                data-testid="create-new-form"
                variant="contained"
                sx={{
                  backgroundColor: '#007bff', // Blue background
                  color: 'white', // White text
                  borderRadius: '30px', // Rounded corners
                  padding: '10px 20px', // Padding for the button
                  fontSize: '16px', // Font size
                  '&:hover': {
                    backgroundColor: '#0056b3', // Darker blue on hover
                  },
                }}
                onClick={handleClickCreate}
              >
                Create New Form
              </ButtonStyled>
              <Menu
                anchorEl={anchorElCreate}
                open={Boolean(anchorElCreate)}
                onClose={handleCloseCreate}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                slotProps={{
                  paper: {
                    sx: {
                      width: anchorElCreate ? `${anchorElCreate.clientWidth}px` : 'auto',
                    },
                  },
                }}
                >
                <MenuItem onClick={handleSubmenuOpen}>Manual</MenuItem>

                <Menu
                  anchorEl={submenuAnchorEl}
                  open={Boolean(submenuAnchorEl)}
                  onClose={handleCloseCreate}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  { languages.map(language => (
                    <MenuItem onClick={() => handleCreate(FileType.NONE,`${language}`)} data-testid={`create-option-${language}`}>
                    Manual ({getLanguageDisplayName(language, frontline?.settings.languageDisplayNames || {})})
                    </MenuItem>
                  ))}
                </Menu>
                <MenuItem onClick={() => handleCreate(FileType.FORM,'en')}>Import Form</MenuItem>
                <MenuItem onClick={handleSubmenuQuestionsOpen}>Import Questions</MenuItem>
                <Menu
                  anchorEl={submenuAnchorQuestionsEl}
                  open={Boolean(submenuAnchorQuestionsEl)}
                  onClose={handleCloseCreate}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  { languages.map(language => (
                    <MenuItem onClick={() => handleCreate(FileType.QUESTIONS,`${language}`)} data-testid={`import-questions-option-${language}`}>
                    Import Questions ({getLanguageDisplayName(language, frontline?.settings.languageDisplayNames || {})})
                    </MenuItem>
                  ))}
                </Menu>

              </Menu>
              <Input
                id={'create_file_input'}
                type="file"
                onChange={handleImportFileChange}
                disabled={false}
                style={{ display: 'none' }}
              />
             </Box>
          </Box>
        </div>
      )}
    </>
  );
};

export default ListView;
